import axios from "axios";

import { isDefined } from "~/shared/common";
import { ChatMessageKind } from "~/shared/enums";
import type {
  Chat,
  ChatMessage,
  ChatMessageCreate,
  ChatMessageUpdate,
  DraftMessage,
  TextMessage,
  UserTextMessage,
} from "~/shared/types";
import type { PartialWithDuid } from "~/shared/typeUtils";
import { makeDuid, makeEmptyLexicalState } from "~/utils/common";
import { chatMessageComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";

export type State = {
  _duidsToChatMessages: Map<string, ChatMessage>;
  _chatDuidsWithoutDrafts: Set<string>;
};

export const state: State = {
  _duidsToChatMessages: new Map(),
  _chatDuidsWithoutDrafts: new Set(),
};

const getPaginationKey = (chatDuid: string) => `chat-messages:${chatDuid}`;
const chatMessagePaginationComparator = (a: ChatMessage, b: ChatMessage) => chatMessageComparator(b, a);

export const makeDraftMessageCreate = (draft: DraftMessage, chatDuid = draft.chatDuid): ChatMessageCreate => ({
  duid: draft.duid,
  chatDuid,
  kind: ChatMessageKind.DRAFT,
  createdAt: draft.createdAt,
  content: draft.content,
  extras: draft.extras,
  attachmentDuids: draft.attachmentDuids,
});

export type Getters = {
  getChatMessageByDuidIfLoaded: (duid: string) => ChatMessage | undefined;
  getChatMessagesByChatDuidOrdered: (chatDuid: string) => ChatMessage[];
  getDraftMessageByChatDuid: (chatDuid: string) => DraftMessage | undefined;
  getTextMessages: (chatDuid: string) => TextMessage[];
  getLoadedChatMessageCount: (chatDuid: string) => number;
  areAllChatMessagesLoaded: (chatDuid: string) => boolean;
  areChatMessagesLoaded: (chatDuid: string) => boolean;
};

export type Actions = {
  loadChatMessagesForChat: (chatDuid: string) => Promise<boolean>;
  loadOlderChatMessagesForChat: (chatDuid: string) => Promise<number>;
  loadDraftMessageForChat: (chatDuid: string) => Promise<DraftMessage | undefined>;
  ensureDraftMessageForChat: (
    chatDuid: string,
    options?: { awaitBackend?: boolean; noBackend?: boolean }
  ) => Promise<DraftMessage | undefined>;
  updateChatDraftMessage: (
    chatDuid: string,
    draft: DraftMessage,
    options?: { awaitBackend?: boolean; noBackend?: boolean }
  ) => Promise<ChatMessage | undefined>;
  addMessageToChatNoBackend: (chatDuid: string, message: UserTextMessage) => void;
  $createChatMessage: (message: ChatMessage) => void;
  $updateChatMessage: (message: PartialWithDuid<ChatMessage>) => void;
  $saveChatMessage: (message: PartialWithDuid<ChatMessage>) => void;
  $deleteChatMessage: (message: ChatMessage) => void;
  $deleteChatMessagesForChat: (chatDuid: string) => void;
  $createDraftMessageNoBackend: (
    chat: Chat,
    draft: DraftMessage,
    options?: { messagesAreKnownEmpty?: boolean }
  ) => DraftMessage;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  getChatMessageByDuidIfLoaded() {
    return (duid) => this._duidsToChatMessages.get(duid);
  },
  getChatMessagesByChatDuidOrdered() {
    return (chatDuid) => {
      const paginationState = this.$getPaginationState(getPaginationKey(chatDuid), null);
      if (!paginationState) {
        return [];
      }

      return [...paginationState.duids.values()]
        .map((duid) => this._duidsToChatMessages.get(duid))
        .filter((message): message is ChatMessage => isDefined(message) && message.kind !== ChatMessageKind.DRAFT)
        .sort(chatMessageComparator);
    };
  },
  getDraftMessageByChatDuid() {
    return (chatDuid) => {
      const currentUserDuid = this.$useUserStore().duid;
      return [...this._duidsToChatMessages.values()].find(
        (message): message is DraftMessage =>
          message.chatDuid === chatDuid &&
          message.kind === ChatMessageKind.DRAFT &&
          message.authorDuid === currentUserDuid
      );
    };
  },
  getTextMessages() {
    return (chatDuid) =>
      this.getChatMessagesByChatDuidOrdered(chatDuid).filter(
        (message): message is TextMessage =>
          message.kind === ChatMessageKind.USER_TEXT || message.kind === ChatMessageKind.ASSISTANT_TEXT
      );
  },
  getLoadedChatMessageCount() {
    return (chatDuid) => this.$getPaginationState(getPaginationKey(chatDuid), null)?.duids.size ?? 0;
  },
  areAllChatMessagesLoaded() {
    return (chatDuid) => this.$getPaginationState(getPaginationKey(chatDuid), null)?.loadedAll ?? false;
  },
  areChatMessagesLoaded() {
    return (chatDuid) => this.$getPaginationState(getPaginationKey(chatDuid), null) !== undefined;
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async loadChatMessagesForChat(chatDuid) {
    if (this.areChatMessagesLoaded(chatDuid)) {
      return true;
    }

    const key = getPaginationKey(chatDuid);

    try {
      const { data } = await this.$backendOld.entityList.chatMessage(
        { chatDuid, isDraft: false, order: "-created_at" },
        0
      );
      data.results.forEach((message) => this.$createChatMessage(message));
      if (!this.$getPaginationState(key, null)) {
        this.$createPaginationState(key, null, chatMessagePaginationComparator);
      }
      this.$addEntitiesToPaginationState(key, null, data.results, data.count);
    } catch (error) {
      if (!axios.isAxiosError(error) || error.response?.status !== 404) {
        throw error;
      }
      const chat = this.getChatByDuidIfLoaded(chatDuid);
      if (chat) {
        this.$deleteChat(chat);
      }
      this.$blacklistEntityDuid("chats", chatDuid);
      return false;
    }

    return true;
  },
  async loadOlderChatMessagesForChat(chatDuid) {
    let paginationState = this.$getPaginationState(getPaginationKey(chatDuid), null);
    if (!paginationState) {
      await this.loadChatMessagesForChat(chatDuid);
      paginationState = this.$getPaginationState(getPaginationKey(chatDuid), null);
      return paginationState ? this.getLoadedChatMessageCount(chatDuid) : 0;
    }
    if (paginationState.loadedAll) {
      return this.getLoadedChatMessageCount(chatDuid);
    }

    const { data } = await this.$backendOld.entityList.chatMessage(
      { chatDuid, isDraft: false, order: "-created_at" },
      this.getLoadedChatMessageCount(chatDuid)
    );
    data.results.forEach((message) => this.$createChatMessage(message));
    this.$addEntitiesToPaginationState(getPaginationKey(chatDuid), null, data.results, data.count);
    return data.count;
  },
  async loadDraftMessageForChat(chatDuid) {
    const currentDraft = this.getDraftMessageByChatDuid(chatDuid);
    if (currentDraft) {
      return currentDraft;
    }
    if (this._chatDuidsWithoutDrafts.has(chatDuid)) {
      return undefined;
    }

    const { data } = await this.$backendOld.entityList.chatMessage({ chatDuid, isDraft: true }, 0, 1);
    const draftFetched = data.results[0];
    if (!draftFetched) {
      this._chatDuidsWithoutDrafts.add(chatDuid);
      return undefined;
    }
    if (draftFetched.kind !== ChatMessageKind.DRAFT) {
      throw new Error(`Expected draft chat message for chat ${chatDuid}.`);
    }

    this.$createChatMessage(draftFetched);
    this._chatDuidsWithoutDrafts.delete(chatDuid);
    return draftFetched;
  },
  async ensureDraftMessageForChat(chatDuid, options = {}) {
    const { awaitBackend = false, noBackend = false } = options;
    const currentDraft = await this.loadDraftMessageForChat(chatDuid);
    if (currentDraft) {
      return currentDraft;
    }

    const chat = this.getChatByDuidIfLoaded(chatDuid);
    if (!chat) {
      return undefined;
    }

    const draft: DraftMessage = {
      duid: makeDuid(),
      chatDuid,
      kind: ChatMessageKind.DRAFT,
      authorDuid: this.$useUserStore().duid,
      content: makeEmptyLexicalState(),
      extras: [],
      createdAt: new Date().toISOString(),
      attachmentDuids: [],
    };
    this.$createDraftMessageNoBackend(chat, draft);

    if (!noBackend) {
      const backendAction = this.$backend.chatMessage.create(makeDraftMessageCreate(draft));
      if (awaitBackend) {
        await backendAction;
      }
    }
    return draft;
  },
  async updateChatDraftMessage(chatDuid, draft, options = {}) {
    const { awaitBackend = false, noBackend = false } = options;
    const currentDraft = this.getDraftMessageByChatDuid(chatDuid);
    if (!currentDraft) {
      return undefined;
    }

    const updatedAt = new Date().toISOString();
    const nextDraft: DraftMessage = {
      ...currentDraft,
      ...draft,
      chatDuid,
      kind: ChatMessageKind.DRAFT,
      updatedAt,
    };
    this.$createChatMessage(nextDraft);

    if (!noBackend) {
      const backendAction = this.$backend.chatMessage.update({
        duid: nextDraft.duid,
        content: nextDraft.content,
        extras: nextDraft.extras,
        attachmentDuids: nextDraft.attachmentDuids,
      } satisfies ChatMessageUpdate);
      if (awaitBackend) {
        await backendAction;
      }
    }
    return nextDraft;
  },
  addMessageToChatNoBackend(chatDuid, message) {
    const chat = this.getChatByDuidIfLoaded(chatDuid);
    const draft = this.getDraftMessageByChatDuid(chatDuid);
    if (!chat || !draft) {
      return;
    }

    const now = new Date().toISOString();
    const currentUserDuid = this.$useUserStore().duid;
    this.$createChatMessage({
      ...message,
      chatDuid,
      authorDuid: message.authorDuid ?? currentUserDuid,
      createdAt: now,
      updatedAt: now,
    });
    this.$createChatMessage({
      ...draft,
      kind: ChatMessageKind.DRAFT,
      chatDuid,
      authorDuid: draft.authorDuid ?? currentUserDuid,
      createdAt: now,
      updatedAt: now,
      extras: chat.extrasAreUserManaged ? message.extras : [],
      attachmentDuids: message.attachmentDuids,
      content: makeEmptyLexicalState(),
    });
    chat.updatedAt = now;
  },
  $createChatMessage(message) {
    const currentMessage = this.getChatMessageByDuidIfLoaded(message.duid);
    if (!currentMessage) {
      this._duidsToChatMessages.set(message.duid, message);
      if (message.kind === ChatMessageKind.DRAFT) {
        this._chatDuidsWithoutDrafts.delete(message.chatDuid);
      } else {
        this.$addEntitiesToPaginationState(getPaginationKey(message.chatDuid), null, [message], null);
      }
      return;
    }

    this.$saveChatMessage(message);
  },
  $updateChatMessage(message) {
    this.$saveChatMessage(message);
  },
  $saveChatMessage(message) {
    const currentMessage = this.getChatMessageByDuidIfLoaded(message.duid);
    if (!currentMessage) {
      return;
    }
    Object.assign(currentMessage, message);
  },
  $deleteChatMessage(message) {
    this._duidsToChatMessages.delete(message.duid);
    if (message.kind === ChatMessageKind.DRAFT) {
      this._chatDuidsWithoutDrafts.add(message.chatDuid);
    }
    this.$removeEntityFromPaginationState(getPaginationKey(message.chatDuid), null, message);
  },
  $deleteChatMessagesForChat(chatDuid) {
    [...this._duidsToChatMessages.values()].forEach((message) => {
      if (message.chatDuid === chatDuid) {
        this._duidsToChatMessages.delete(message.duid);
      }
    });
    this._chatDuidsWithoutDrafts.delete(chatDuid);
    this.$deletePaginationStates(getPaginationKey(chatDuid));
  },
  $createDraftMessageNoBackend(chat, draft, options = {}) {
    if (options.messagesAreKnownEmpty) {
      const key = getPaginationKey(chat.duid);
      this.$createPaginationState(key, null, chatMessagePaginationComparator);
      this.$addEntitiesToPaginationState(key, null, [], 0);
    }
    const nextDraft: DraftMessage = {
      ...draft,
      chatDuid: chat.duid,
      authorDuid: draft.authorDuid ?? this.$useUserStore().duid,
    };
    this.$createChatMessage(nextDraft);
    return nextDraft;
  },
};

export { actions, getters };
